/* eslint-disable */
// @ts-nocheck
import { Container } from 'pixi.js';
import { Signal } from './Signal';
import { TweenData, TweenDataState } from './TweenData';
import { Easing, EasingFunction, EasingMap } from './easing';
import { TweenManager } from './TweenManager';

type UpdateCallback = (tween?: Tween, value?: number, tweenData?: TweenData) => unknown;

export class Tween {
  public readonly onStart: Signal<[object, this]> = new Signal();

  public readonly onLoop: Signal<[object, this]> = new Signal();

  public readonly onRepeat: Signal<[object, this]> = new Signal();

  public readonly onChildComplete: Signal<[object, this]> = new Signal();

  public readonly onComplete: Signal<[object, this]> = new Signal();

  public readonly onRemoved = new Signal();

  public readonly onUpdate: Signal<[object, this]> = new Signal();

  private readonly manager: TweenManager;

  public readonly target: object;

  private readonly isTargetContainer: boolean;

  public properties: object = {};

  public reverse = false;

  public timeScale = 1;

  public chainedTween: Tween;

  public waitingToRepeat = false;

  private timeline: TweenData[] = [];

  private repeatCounter = 0;

  public pendingDelete = false;

  public isRunning = false;

  private current = 0;

  private isPaused = false;

  private pausedTime = 0;

  private codePaused = false;

  private hasStarted = false;

  private updateCallback: UpdateCallback | null = null;

  constructor(target: object, manager: TweenManager) {
    this.target = target;
    this.isTargetContainer = target instanceof Container;
    this.manager = manager;
  }

  public getCurrentTime(): number {
    return this.manager.currentTime;
  }

  public to(
    props: object,
    duration = 1000,
    ease: EasingFunction | keyof typeof EasingMap | null = Easing.Default,
    autoStart = false,
    delay = 0,
    repeat = 0,
    yoyo = false,
  ): this {
    ease = ease || Easing.Default;
    duration = Math.max(1, duration);

    if (this.isRunning) {
      return this;
    }

    if (typeof ease === 'string') {
      ease = EasingMap[ease] || Easing.Default;
    }

    this.timeline.push(new TweenData(this).to(props, duration, ease, delay, repeat, yoyo));

    if (autoStart) this.start();

    return this;
  }

  public from(
    props: object,
    duration = 1000,
    ease: EasingFunction | keyof typeof EasingMap | null = Easing.Default,
    autoStart = false,
    delay = 0,
    repeat = 0,
    yoyo = false,
  ): this {
    ease = ease || Easing.Default;
    duration = Math.max(1, duration);

    if (this.isRunning) {
      return this;
    }

    if (typeof ease === 'string' || ease === null) {
      ease = EasingMap[ease] || Easing.Default;
    }

    this.timeline.push(new TweenData(this).from(props, duration, ease, delay, repeat, yoyo));

    if (autoStart) this.start();

    return this;
  }

  public start(index = 0): this {
    if (this.target instanceof Container && this.target.destroyed) {
      return this;
    }

    if (this.target === null || this.timeline.length === 0 || this.isRunning) {
      return this;
    }

    for (const timeline of this.timeline) {
      for (const property in timeline.vEnd) {
        if (!timeline.vEnd.hasOwnProperty(property)) continue;

        this.properties[property] = this.target[property] || 0;
        if (!Array.isArray(this.properties[property])) {
          //  Ensures we're using numbers, not strings
          this.properties[property] *= 1.0;
        }
      }
    }

    for (const timeline of this.timeline) {
      timeline.loadValues();
    }

    this.manager.add(this);

    this.isRunning = true;

    if (index < 0 || index > this.timeline.length - 1) {
      index = 0;
    }

    this.current = index;

    this.timeline[this.current].start();

    return this;
  }

  public async asyncOnComplete(): Promise<void> {
    await this.onComplete.wait();
  }

  public stop(complete = false): this {
    this.isRunning = false;
    this.updateCallback = null;
    // update context

    if (complete) {
      this.onComplete.dispatch([this.target, this]);
      this.hasStarted = false;

      if (this.chainedTween) {
        this.chainedTween.start();
      }
    }

    this.manager.remove(this);

    return this;
  }

  public updateTweenData(property: string, value: unknown, index = 0): this {
    if (this.timeline.length === 0) {
      return this;
    }

    if (index === -1) {
      for (const timeline of this.timeline) {
        timeline[property] = value;
      }
    } else {
      this.timeline[index][property] = value;
    }

    return this;
  }

  public delay(duration: number, index = 0): this {
    return this.updateTweenData('delay', duration, index);
  }

  public repeat(total: number, repeatDelay: number | number[] = 0, index = 0): this {
    this.updateTweenData('repeatCounter', total, index);
    return this.updateTweenData('repeatDelay', repeatDelay, index);
  }

  public repeatDelay(duration: number | number[], index = 0): this {
    return this.updateTweenData('repeatDelay', duration, index);
  }

  public yoyo(enable: boolean, yoyoDelay = 0, index = 0): this {
    this.updateTweenData('yoyo', enable, index);
    return this.updateTweenData('yoyoDelay', yoyoDelay, index);
  }

  public yoyoDelay(duration: number, index = 0): this {
    return this.updateTweenData('yoyoDelay', duration, index);
  }

  public easing(ease: EasingFunction | string, index = 0): this {
    if (typeof ease === 'string') {
      ease = EasingMap[ease] || Easing.Default;
    }
    return this.updateTweenData('easingFunction', ease, index);
  }

  public interpolation(interpolation: InterpolationFunction | string, index = 0): this {
    if (typeof interpolation === 'string') {
      interpolation = InterpolationMap[interpolation] || linearInterpolation;
    }
    this.updateTweenData('interpolationFunction', interpolation, index);
    return this;
  }

  public repeatAll(total = 0): this {
    this.repeatCounter = total;
    return this;
  }

  public chain(...tweens: Tween[]): this {
    let i: number = tweens.length;

    while (i--) {
      if (i > 0) {
        tweens[i - 1].chainedTween = tweens[i];
      } else {
        this.chainedTween = tweens[i];
      }
    }

    return this;
  }

  public loop(value = true): this {
    this.repeatCounter = value ? -1 : 0;
    return this;
  }

  public onUpdateCallback(callback: UpdateCallback): this {
    this.updateCallback = callback;
    return this;
  }

  public pause(): this {
    this.isPaused = true;
    this.codePaused = true;
    this.pausedTime = this.manager.currentTime;
    return this;
  }

  public resume(): this {
    if (this.isPaused) {
      this.isPaused = false;
      this.codePaused = false;
    }
    return this;
  }

  public fastForward(deltaTime: number): void {
    this.update(Date.now(), deltaTime);
  }

  public update(time: number, deltaTime: number): boolean {
    if (this.pendingDelete || !this.target) {
      return false;
    }

    if (this.isPaused) {
      return true;
    }

    if (this.isTargetContainer && (this.target as Container).destroyed) {
      this.stop();
      return false;
    }

    const status: TweenDataState = this.timeline[this.current].update(time, deltaTime);
    this.waitingToRepeat = false;

    if (status === TweenDataState.Pending) {
      return true;
    }
    if (status === TweenDataState.WaitingToRepeat) {
      this.waitingToRepeat = true;
      return this.updateRunning();
    }
    if (status === TweenDataState.Running) {
      return this.updateRunning();
    }
    if (status === TweenDataState.Looped) {
      return this.updateLooped();
    }
    if (status === TweenDataState.Complete) {
      return this.updateComplete();
    }

    return false;
  }

  private updateLooped(): boolean {
    const reversed = this.timeline[this.current].inReverse;
    if (reversed) return true;

    if (this.timeline[this.current].repeatCounter === -1) {
      this.onLoop.dispatch([this.target, this]);
    } else {
      this.onRepeat.dispatch([this.target, this]);
    }

    return true;
  }

  private updateRunning(): boolean {
    if (!this.hasStarted) {
      this.onStart.dispatch([this.target, this]);
      this.hasStarted = true;
    }

    if (this.updateCallback) {
      this.updateCallback(this, this.timeline[this.current].value, this.timeline[this.current]);
    }

    this.onUpdate.dispatch([this.target, this]);

    //  In case the update callback modifies this tween
    return this.isRunning;
  }

  private updateComplete(): boolean {
    let complete = false;

    if (this.updateCallback) {
      this.updateCallback(this, this.timeline[this.current].value, this.timeline[this.current]);
    }

    //  What now?
    if (this.reverse) {
      this.current--;

      if (this.current < 0) {
        this.current = this.timeline.length - 1;
        complete = true;
      }
    } else {
      this.current++;

      if (this.current === this.timeline.length) {
        this.current = 0;
        complete = true;
      }
    }

    if (complete) {
      //  We've reached the start or end of the child tweens (depending on Tween.reverse), should we repeat it?
      if (this.repeatCounter === -1) {
        this.timeline[this.current].start();
        this.onLoop.dispatch([this.target, this]);
        return true;
      }
      if (this.repeatCounter > 0) {
        this.repeatCounter--;

        this.timeline[this.current].start();
        this.onRepeat.dispatch([this.target, this]);
        return true;
      }
      //  No more repeats and no more children, so we're done
      this.isRunning = false;
      this.onComplete.dispatch([this.target, this]);
      this.hasStarted = false;

      if (this.chainedTween) {
        this.chainedTween.start();
      }

      return false;
    }
    //  We've still got some children to go
    this.onChildComplete.dispatch([this.target, this]);
    this.timeline[this.current].start();
    return true;
  }

  public pauseOnGamePause(): void {
    if (!this.codePaused) {
      this.isPaused = true;
      this.pausedTime = this.manager.currentTime;
    }
  }

  public resumeOnGameResume(): void {
    if (!this.codePaused) {
      this.resume();
    }
  }
}
